import { useState, type FC } from "react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Activity } from "lucide-react";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import { useNavigate } from "react-router-dom";
import SessionStorageHelper from "../../utils/sessionStorageHelper";

// Constants
const DIABETES_TYPES = ["Type 1", "Type 2", "Prediabetic", "None"] as const;

type DiabetesType = (typeof DIABETES_TYPES)[number];

interface DiabetesTypeInfo {
  displayName: string;
  apiValue: string;
  information: string;
  nextRoute: string;
}

const DIABETES_TYPE_DATA: Record<DiabetesType, DiabetesTypeInfo> = {
  "Type 1": {
    displayName: "Type 1",
    apiValue: "type1",
    information:
      "Typically genetic or hereditary in nature. The body's immune system attacks insulin-producing cells.",
    nextRoute: GluceraNavigationRoutes.ADDMEDICATIONPAGE,
  },
  "Type 2": {
    displayName: "Type 2",
    apiValue: "type2",
    information:
      "More influenced by lifestyle factors such as diet, physical inactivity, and obesity.",
    nextRoute: GluceraNavigationRoutes.ADDMEDICATIONPAGE,
  },
  Prediabetic: {
    displayName: "Prediabetic",
    apiValue: "prediabetic",
    information:
      "Higher-than-normal blood sugar levels, but not high enough for Type 2 diabetes.",
    nextRoute: GluceraNavigationRoutes.ADDMEDICATIONPAGE,
  },
  None: {
    displayName: "None",
    apiValue: "none",
    information: "No diabetes diagnosis",
    nextRoute: GluceraNavigationRoutes.WEIGHT_LOSS,
  },
};

// Information Section Component
interface InformationSectionProps {
  title: string;
  info: string;
}

const InformationSection: FC<InformationSectionProps> = ({ info }) => (
  <div className="mt-3">
    <p className="text-xs text-muted-foreground">{info}</p>
  </div>
);

const Typeofdiabetes: FC = () => {
  const [selected, setSelected] = useState<DiabetesType | "">("");
  const navigate = useNavigate();

  const handleSave = () => {
    if (selected) {
      const typeData = DIABETES_TYPE_DATA[selected];
      if (typeData) {
        SessionStorageHelper.saveField("diabetes_type", typeData.apiValue);
        navigate("/glucera" + typeData.nextRoute);
      }
    }
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 p-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-muted-foreground">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 5 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="mb-4 space-y-2">
            <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
              Type Of Your Diabetes
            </h1>
            <p className="text-sm text-muted-foreground">
              Select your type of diabetes
            </p>
          </div>

          <div className="space-y-3 pb-4">
            {DIABETES_TYPES.map(option => (
              <Card
                key={option}
                className={`cursor-pointer p-4 transition-all hover:shadow-md ${
                  selected === option
                    ? "border-2 border-primary bg-primary/5"
                    : "border-2 border-border hover:border-primary/50"
                }`}
                onClick={() => setSelected(option)}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                      selected === option
                        ? "border-primary bg-primary"
                        : "border-muted-foreground"
                    }`}
                  >
                    {selected === option && (
                      <div className="h-2.5 w-2.5 rounded-full bg-primary-foreground" />
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-lg font-semibold text-foreground">
                      {option}
                    </span>
                    {selected === option && DIABETES_TYPE_DATA[option] && (
                      <InformationSection
                        title="Information"
                        info={DIABETES_TYPE_DATA[option].information}
                      />
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Footer Button */}
        <div className="pb-safe border-t p-4">
          <Button
            onClick={handleSave}
            disabled={!selected}
            className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Typeofdiabetes;
